import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SemanaDia } from '@/types';
import { formatMes, monthOf, parseISODate, todayISO } from '@/lib/format';
import type { Language } from '@/i18n';
import { Icon } from './ui/Icon';

const DOW = {
  pt: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};

/** Monday-indexed weekday, so a strip that spans several weeks labels itself. */
const dowIndex = (iso: string) => (parseISODate(iso).getDay() + 6) % 7;

interface Props {
  dias: SemanaDia[];
  selecionado: string;
  onSelect: (data: string) => void;
  /** Called when the strip is swiped to its left edge — pulls in older weeks. */
  onMaisSemanas?: () => void;
  aCarregar?: boolean;
}

/**
 * The day strip: seven days on screen, but it scrolls back through the previous
 * weeks — "deslizar a barra para consultar os dias e semanas anteriores".
 *
 * A dot marks a day that already has hours; days outside the editing window are
 * dimmed so the lock is visible before you tap. The month above the strip
 * follows whatever is in view, and *Hoje* brings the selection back.
 */
export function WeekStrip({ dias, selecionado, onSelect, onMaisSemanas, aCarregar = false }: Props) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'pt') as Language;
  const labels = DOW[lang === 'en' ? 'en' : 'pt'];
  const hoje = todayISO();

  const scroller = useRef<HTMLDivElement>(null);
  const larguraAnterior = useRef(0);
  const centradoEm = useRef<string | null>(null);
  const [mesVisivel, setMesVisivel] = useState(() => monthOf(selecionado));
  const [noFim, setNoFim] = useState(true);

  // Older weeks arrive on the *left*, which would slide everything sideways
  // under the finger. Give back exactly what was prepended.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const crescimento = el.scrollWidth - larguraAnterior.current;
    if (larguraAnterior.current > 0 && crescimento > 0) el.scrollLeft += crescimento;
    larguraAnterior.current = el.scrollWidth;
  }, [dias.length]);

  // Centre the selected day — on first paint without animation, afterwards with.
  // Never re-centres for a day already centred, so loading older weeks does not
  // yank the strip back to the selection.
  useEffect(() => {
    if (centradoEm.current === selecionado) return;
    const el = scroller.current;
    const alvo = el?.querySelector<HTMLElement>(`[data-dia="${selecionado}"]`);
    if (!el || !alvo) return;
    el.scrollTo({
      left: alvo.offsetLeft - (el.clientWidth - alvo.offsetWidth) / 2,
      behavior: centradoEm.current === null ? 'auto' : 'smooth',
    });
    centradoEm.current = selecionado;
  }, [selecionado, dias]);

  const aoScroll = () => {
    const el = scroller.current;
    if (!el || dias.length === 0) return;

    const passo = el.scrollWidth / dias.length;
    const indice = Math.min(dias.length - 1, Math.max(0, Math.floor(el.scrollLeft / passo)));
    const mes = monthOf(dias[indice].data);
    setMesVisivel((atual) => (atual === mes ? atual : mes));
    setNoFim(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);

    if (el.scrollLeft <= 24 && onMaisSemanas && !aCarregar) onMaisSemanas();
  };

  /** One screenful is exactly one week, so the arrows step week by week. */
  const deslizar = (direcao: -1 | 1) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: direcao * el.clientWidth, behavior: 'smooth' });
  };

  const setaClasses =
    'grid h-[26px] w-[26px] place-items-center rounded-lg border border-conduril-border bg-white text-conduril disabled:opacity-35';

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="label-eyebrow truncate">{formatMes(mesVisivel, lang)}</span>
        <div className="flex shrink-0 items-center gap-1.5">
          {selecionado !== hoje && (
            <button
              type="button"
              onClick={() => onSelect(hoje)}
              className="rounded-lg border border-conduril-border bg-white px-2.5 py-1 text-[10px]
                         font-semibold uppercase tracking-wider text-conduril"
            >
              {t('common.today')}
            </button>
          )}
          <button
            type="button"
            aria-label="previous week"
            onClick={() => deslizar(-1)}
            className={setaClasses}
          >
            <Icon name="chevron_left" className="text-[16px]" />
          </button>
          <button
            type="button"
            aria-label="next week"
            disabled={noFim}
            onClick={() => deslizar(1)}
            className={setaClasses}
          >
            <Icon name="chevron_right" className="text-[16px]" />
          </button>
        </div>
      </div>

      <div
        ref={scroller}
        onScroll={aoScroll}
        className="no-scrollbar flex snap-x gap-1.5 overflow-x-auto overscroll-x-contain"
      >
        {dias.map((dia) => {
          const ativo = dia.data === selecionado;
          const eHoje = dia.data === hoje;
          const numero = parseISODate(dia.data).getDate();
          return (
            <button
              key={dia.data}
              type="button"
              data-dia={dia.data}
              onClick={() => onSelect(dia.data)}
              aria-pressed={ativo}
              className={`flex w-[calc((100%-2.25rem)/7)] shrink-0 snap-start flex-col items-center gap-1.5
                rounded-xl border py-2.5 transition
              ${
                ativo
                  ? 'border-conduril bg-conduril text-white'
                  : dia.editavel
                    ? `bg-white text-conduril-ink ${eHoje ? 'border-conduril' : 'border-conduril-border'}`
                    : `bg-[#eeeff4] text-[#a4a9b6] ${eHoje ? 'border-conduril' : 'border-conduril-border'}`
              }`}
            >
              <span className="text-[9px] font-medium uppercase tracking-wider opacity-70">
                {labels[dowIndex(dia.data)]}
              </span>
              <span className="num text-[15px] font-semibold leading-none">
                {String(numero).padStart(2, '0')}
              </span>
              <span
                className="h-[5px] w-[5px] rounded-full"
                style={{
                  background: dia.temRegistos ? (ativo ? '#fff' : '#14257d') : 'transparent',
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
